import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useDeleteContractOriginalMutation,
  useDeleteTaxCertificateMutation,
} from "src/entities/partnerContracts/model/api/api";
import { useGetPartnerContractDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { useGetPartnerQuery } from "src/shared/api/partner";
import {
  AutocompleteField,
  DatePickerField,
  EConfirmModalType,
  LoadingButton,
  NumberTextField,
  SelectField,
} from "src/shared/components";
import { originalAvailabilityOptions } from "src/shared/constants/selectOptions/contract";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { useManagerSearch } from "src/shared/hooks/useManagerSearch";
import { EAgencies } from "src/shared/types/common.types";
import { ETaxSystem } from "src/shared/types/partner.types";
import { messages, regexps } from "src/shared/utils/hookFormRules";
import { IPartnerComplexContractFormValues } from "../../../model/types";
import { ComplexContractFormCustomerBlock } from "./ComplexContractFormCustomerBlock";

// todo порефакторить этот компонент слишком большой

interface IContractFormProps {
  agency: EAgencies;
  isEdit?: boolean;
  handleDownloadDocxTemplate?: () => void;
}
export function ComplexContractForm({
  agency,
  isEdit,
  handleDownloadDocxTemplate,
}: IContractFormProps) {
  const { partner_id, contract_id } = useParams();

  const { data: partnerData } = useGetPartnerQuery(partner_id!, {
    skip: !partner_id,
  });

  const { data: contractDropdowns } = useGetPartnerContractDropdownsQuery(
    partner_id ?? skipToken,
  );

  const methods = useFormContext<IPartnerComplexContractFormValues>();
  const { control, trigger, watch, setValue } = methods;

  const { contract_state, currency, tax_system, customers_info } =
    contractDropdowns || {};

  const contractManager = useWatch({
    control,
    name: "contract_manager",
  });

  const contextTeamlead = useWatch({
    control,
    name: "context_teamlead",
  });

  const accountManager = useWatch({
    control,
    name: "account_manager",
  });

  const selectedManagerId =
    contractManager || contextTeamlead || accountManager || "";

  const { autocompleteProps } = useManagerSearch({
    mainManagerId: selectedManagerId,
  });

  const isContractOriginalFileEnabled = watch("contract_original_file_enabled");
  const isTaxCertificateEnabled = watch("tax_certificate_enabled");
  const contractOriginalFile = watch("contract_original_file_path");
  const taxCertificateFile = watch("tax_certificate_path");
  const contractOriginalAttachDate = watch("contract_original_attach_date");
  const taxCertificateAttachDate = watch("tax_certificate_attach_date");

  const formatAttachDate = (val?: string | null) =>
    val ? new Date(val).toLocaleDateString("ru-RU") : "—";

  const isIE = partnerData?.organization_form.value === 3;

  const currentContractOriginalFile =
    typeof contractOriginalFile === "string" ? contractOriginalFile : null;

  const currentTaxCertificateFile =
    typeof taxCertificateFile === "string" ? taxCertificateFile : null;

  const [
    deleteContractOriginal,
    { isLoading: isLoadingDeleteContractOriginal },
  ] = useDeleteContractOriginalMutation();

  const [deleteTaxCertificate, { isLoading: isLoadingDeleteTaxCertificate }] =
    useDeleteTaxCertificateMutation();

  const {
    modal: deleteContractOriginalConfirmationModal,
    showModal: showDeleteContractOriginalConfirmationModal,
    closeModal: closeDeleteContractOriginalConfirmationModal,
  } = useConfirmModal({
    onResolve: async () => {
      try {
        await deleteContractOriginal({
          partner_id: Number(partner_id),
          contract_id: Number(contract_id),
        }).unwrap();

        methods.setValue("contract_original_file_path", null);
        methods.setValue("contract_original_file_enabled", false);
        closeDeleteContractOriginalConfirmationModal();
      } catch (e) {
        showApiError(e);
      }
    },
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: {
      isLoading: isLoadingDeleteContractOriginal,
      title: "Удалить",
    },
    children: (
      <Typography>Вы уверены, что хотите удалить файл договора?</Typography>
    ),
    title: "Подтверждение удаления",
  });

  const {
    modal: deleteTaxCertificateConfirmationModal,
    showModal: showDeleteTaxCertificateConfirmationModal,
    closeModal: closeDeleteTaxCertificateConfirmationModal,
  } = useConfirmModal({
    onResolve: async () => {
      try {
        await deleteTaxCertificate({
          partner_id: Number(partner_id),
          contract_id: Number(contract_id),
        }).unwrap();

        setValue("tax_certificate_path", null);
        setValue("tax_certificate_enabled", false);
        closeDeleteTaxCertificateConfirmationModal();
      } catch (e) {
        showApiError(e);
      }
    },
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: {
      isLoading: isLoadingDeleteTaxCertificate,
      title: "Удалить",
    },
    children: (
      <Typography>
        Вы уверены, что хотите удалить справку о налогообложении?
      </Typography>
    ),
    title: "Подтверждение удаления",
  });

  const handleContractOriginalToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.checked && currentContractOriginalFile) {
      showDeleteContractOriginalConfirmationModal();
    } else {
      methods.setValue("contract_original_file_enabled", e.target.checked);
    }
  };

  const handleTaxCertificateToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.checked && currentTaxCertificateFile) {
      showDeleteTaxCertificateConfirmationModal();
    } else {
      methods.setValue("tax_certificate_enabled", e.target.checked);
    }
  };

  const formatFileName = (fileUrl: string): JSX.Element | null => {
    if (!fileUrl) return null;

    const pathWithoutQuery = fileUrl.split("?")[0];
    const pathSegments = pathWithoutQuery.split("/");
    const fullFileName = pathSegments[pathSegments.length - 1];

    let shortenedFileName = fullFileName;

    if (fullFileName.length > 15) {
      const firstPart = fullFileName.slice(0, 7);
      const lastPart = fullFileName.slice(-9);
      shortenedFileName = `${firstPart}…${lastPart}`;
    }

    return (
      <Link href={fileUrl} target="_blank" rel="noopener noreferrer">
        {shortenedFileName}
      </Link>
    );
  };

  useEffect(() => {
    if (isEdit) {
      if (currentContractOriginalFile) {
        setValue("contract_original_file_enabled", true);
      }

      if (currentTaxCertificateFile) {
        setValue("tax_certificate_enabled", true);
      }
    }
  }, [
    isEdit,
    currentContractOriginalFile,
    currentTaxCertificateFile,
    setValue,
  ]);

  useEffect(() => {
    if (!isEdit && contract_state?.length) {
      setValue("contract_state", 3);
    }
  }, [contract_state, isEdit, setValue]);

  return (
    <Stack direction="column" spacing={4}>
      {deleteContractOriginalConfirmationModal}
      {deleteTaxCertificateConfirmationModal}

      {isEdit && (
        <Controller
          control={control}
          name="contract_number"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              inputRef={ref}
              fullWidth
              {...field}
              label="№ Договора"
              error={!!error}
              helperText={error?.message}
              disabled
            />
          )}
        />
      )}

      <Controller
        name="contract_date"
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <DatePickerField
            inputRef={ref}
            {...field}
            disableFuture
            error={error}
            label="Дата договора"
          />
        )}
        rules={{ required: messages.required }}
      />

      <Controller
        control={control}
        name="site"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Сайт"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="contract_state"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Статус договора"
            error={!!error}
            helperText={error?.message}
            options={contract_state}
          />
        )}
        rules={{ required: messages.required }}
      />

      <Controller
        control={control}
        name="original_availability"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Наличие оригинала"
            error={!!error}
            helperText={error?.message}
            options={originalAvailabilityOptions}
          />
        )}
        rules={{ required: messages.required }}
      />

      <Controller
        control={control}
        name="currency"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Валюта"
            error={!!error}
            helperText={error?.message}
            options={currency}
          />
        )}
        rules={{ required: messages.required }}
      />

      <Controller
        control={control}
        name="tax_value"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <NumberTextField
            inputRef={ref}
            fullWidth
            {...field}
            label="НДС"
            error={!!error}
            helperText={error?.message}
            decimalScale={0}
            allowNegative={false}
          />
        )}
        rules={{
          validate: (val, formValues) => {
            if (formValues.tax_system === ETaxSystem._1 && val <= 0) {
              return "НДС должен быть больше 0 если выбрано 'с НДС'";
            }

            if (formValues.tax_system === ETaxSystem._0 && val > 0) {
              return "НДС не может быть больше 0 если выбрано 'без НДС'";
            }
          },
        }}
      />

      <Controller
        control={control}
        name="contract_email"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Email"
            error={!!error}
            helperText={error?.message}
          />
        )}
        rules={{
          pattern: { value: regexps.emailRegexp, message: messages.email },
        }}
      />

      <Controller
        control={control}
        name="tax_system"
        render={({ field }) => (
          <FormControl
            sx={{
              gap: 2,
              flexDirection: { xs: "column", xl: "row" },
              alignItems: { xs: "flex-start", xl: "center" },
            }}
          >
            <FormLabel id="tax_system">
              <Typography sx={{ color: "text.secondary" }}>
                Система налогообложения
              </Typography>
            </FormLabel>

            <RadioGroup
              aria-labelledby="tax_system"
              name="tax_system"
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
                trigger("tax_value");
              }}
              row
            >
              {tax_system?.map((item) => (
                <FormControlLabel
                  key={item.value}
                  value={item.value}
                  control={<Radio />}
                  label={item.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="note"
        render={({ field: { ref, ...field } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Примечание"
            multiline
            rows={3}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="partner_bonus_min_sum"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <NumberTextField
            inputRef={ref}
            fullWidth
            {...field}
            min={0}
            label="Минимальный платеж (без НДС)"
            error={!!error}
            helperText={error?.message}
            decimalScale={0}
            allowNegative={false}
          />
        )}
        rules={{ required: messages.required }}
      />

      <Typography>Команда</Typography>

      <Controller
        control={control}
        name="contract_manager"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <AutocompleteField<number, false>
            fullWidth
            {...field}
            inputRef={ref}
            label="Договор"
            error={!!error}
            helperText={error?.message}
            value={field.value || null}
            {...autocompleteProps}
          />
        )}
      />

      <Controller
        control={control}
        name="context_teamlead"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <AutocompleteField<number, false>
            fullWidth
            {...field}
            inputRef={ref}
            label="Teamlead"
            error={!!error}
            helperText={error?.message}
            value={field.value || null}
            {...autocompleteProps}
          />
        )}
      />

      <Controller
        control={control}
        name="account_manager"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <AutocompleteField<number, false>
            fullWidth
            {...field}
            inputRef={ref}
            label="Аккаунт"
            error={!!error}
            helperText={error?.message}
            value={field.value || null}
            {...autocompleteProps}
          />
        )}
      />

      <Typography>Заказчик</Typography>

      <ComplexContractFormCustomerBlock
        customers_info={customers_info}
        agency={agency}
      />

      <Typography>Исполнитель</Typography>

      <Controller
        control={control}
        name="executor_info.represented_by"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="В лице"
            error={!!error}
            helperText={error?.message}
          />
        )}
        rules={{ required: messages.required }}
      />

      <Controller
        control={control}
        name="executor_info.position"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Должность"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="executor_info.bases_on"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="На основании"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="executor_info.signs"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Подписывает"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Typography>Файлы</Typography>

      <Controller
        name="contract_original_file_enabled"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleContractOriginalToggle(e);
                }}
              />
            }
            label="Договор с правками"
          />
        )}
      />

      {isContractOriginalFileEnabled && (
        <Controller
          name="contract_original_file_path"
          control={control}
          rules={{
            validate: (file) => {
              if (typeof file === "string") return true;

              if (!file && !currentContractOriginalFile) {
                return messages.taxAndContractOriginal;
              }

              if (file) {
                const allowedTypes = ["application/pdf"];

                if (!allowedTypes.includes(file.type)) {
                  return "Допустимые форматы: PDF";
                }

                if (file.size > 10_000_000) {
                  return messages.maxFileSizeMb;
                }
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              {isEdit && currentContractOriginalFile ? (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Дата прикрепления файла:{" "}
                    {formatAttachDate(contractOriginalAttachDate)}
                  </Typography>
                  <LoadingButton
                    onClick={handleDownloadDocxTemplate}
                    variant="outlined"
                  >
                    Скачать шаблон
                  </LoadingButton>
                </>
              ) : (
                <TextField
                  type="file"
                  inputRef={field.ref}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(
                      (e.target as HTMLInputElement).files?.[0] || null,
                    );
                  }}
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                    children: "Прикрепите файл PDF",
                  }}
                  inputProps={{
                    accept: "application/pdf",
                  }}
                />
              )}
            </>
          )}
        />
      )}

      {isIE && (
        <>
          <Controller
            name="tax_certificate_enabled"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      handleTaxCertificateToggle(e);
                    }}
                  />
                }
                label="Справка о налогообложении"
              />
            )}
          />

          {isTaxCertificateEnabled && (
            <Controller
              name="tax_certificate_path"
              control={control}
              rules={{
                validate: (file) => {
                  if (typeof file === "string") return true;

                  if (!file && !currentTaxCertificateFile) {
                    return messages.taxAndContractOriginal;
                  }

                  if (file) {
                    const allowedTypes = ["application/pdf"];

                    if (!allowedTypes.includes(file.type)) {
                      return "Допустимые форматы: PDF";
                    }

                    if (file.size > 10_000_000) {
                      return messages.maxFileSizeMb;
                    }
                  }

                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  {isEdit && currentTaxCertificateFile ? (
                    <>
                      <Typography variant="body2">
                        Текущий файл:{" "}
                        {formatFileName(currentTaxCertificateFile)}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Дата прикрепления файла:{" "}
                        {formatAttachDate(taxCertificateAttachDate)}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      type="file"
                      inputRef={field.ref}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(
                          (e.target as HTMLInputElement).files?.[0] || null,
                        );
                      }}
                      error={!!error}
                      helperText={error?.message}
                      InputLabelProps={{
                        shrink: true,
                        children: "Прикрепите файл PDF",
                      }}
                      inputProps={{
                        accept: "application/pdf",
                      }}
                    />
                  )}
                </>
              )}
            />
          )}
        </>
      )}
    </Stack>
  );
}
